/**
 * Personelin belgelerini listeler.
 * Resim belgeler için thumbnail önizleme.
 * Download butonu gerçek dosya indirmesi (blob fetch).
 */

import { useCallback, useEffect, useState } from 'react'
import {
  FileText, Download, Trash2, Plus, AlertCircle, ExternalLink, Calendar,
} from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAuth } from '@/contexts/AuthContext'
import {
  personelBelgeleri, belgeSil,
  BELGE_TIPI_ETIKETLERI,
  type Belge,
} from '@/lib/services/belge-service'
import { BelgeYukleModal } from './BelgeYukleModal'
import { YETKILER } from '@/lib/yetkiler'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  personel: KullaniciPersonel | null
  onKapat: () => void
}

export function BelgelerModal({ acik, personel, onKapat }: Props) {
  const { kullanici, firebaseUser } = useAuth()
  const [belgeler, setBelgeler] = useState<Belge[]>([])
  const [yukleniyor, setYukleniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [yukleModalAcik, setYukleModalAcik] = useState(false)
  const [silinecek, setSilinecek] = useState<Belge | null>(null)

  const isBoss = kullanici?.rol === 'boss'
  const yoneticiYetkileri = kullanici?.rol === 'personel'
    ? ((kullanici as KullaniciPersonel).yoneticiYetkileri ?? [])
    : []
  const yukleyebilir = isBoss || yoneticiYetkileri.includes(YETKILER.PERSONEL_BELGE_YUKLE)
  const silebilir = isBoss || yoneticiYetkileri.includes(YETKILER.PERSONEL_BELGE_SIL)

  const yukle = useCallback(async () => {
    if (!personel) return
    try {
      setYukleniyor(true)
      const list = await personelBelgeleri(personel.id)
      setBelgeler(list)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Belgeler yüklenemedi')
    } finally {
      setYukleniyor(false)
    }
  }, [personel])

  useEffect(() => {
    if (acik) yukle()
  }, [acik, yukle])

  async function silOnay() {
    if (!silinecek || !firebaseUser) return
    try {
      await belgeSil(silinecek.id, firebaseUser.uid)
      setSilinecek(null)
      await yukle()
    } catch (err) {
      alert('Silinemedi: ' + (err as Error).message)
    }
  }

  const [indiriliyor, setIndiriliyor] = useState<string | null>(null)

  /** Download — Cloud Function proxy üzerinden (Content-Disposition: attachment). */
  async function indir(belge: Belge) {
    if (!firebaseUser) return
    setIndiriliyor(belge.id)
    try {
      const idToken = await firebaseUser.getIdToken()
      const params = new URLSearchParams({
        yol: belge.dosyaYolu,
        ad: belge.dosyaAdi,
      })
      const url = `https://europe-west3-shiftbeat-prod.cloudfunctions.net/belgeIndir?${params}`

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${idToken}` },
      })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = belge.dosyaAdi
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch (err) {
      alert('İndirilemedi: ' + (err as Error).message)
    } finally {
      setIndiriliyor(null)
    }
  }

  function gunSayisi(tarih: string): number {
    const hedef = new Date(tarih).getTime()
    const simdi = new Date().setHours(0, 0, 0, 0)
    return Math.round((hedef - simdi) / (1000 * 60 * 60 * 24))
  }

  if (!personel) return null

  return (
    <>
      <Modal acik={acik} onKapat={onKapat} baslik={`Belgeler — ${personel.isim}`} boyut="lg">
        <div>
          {yukleyebilir && (
            <button
              onClick={() => setYukleModalAcik(true)}
              className="btn-primary w-full mb-4 inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Yeni Belge Yükle
            </button>
          )}

          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3 mb-3">
              {hata}
            </div>
          )}

          {yukleniyor && belgeler.length === 0 ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
            </div>
          ) : belgeler.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-700">Henüz belge yok</p>
              {!yukleyebilir && (
                <p className="text-xs text-gray-500 mt-1">Belge yükleme yetkin yok</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {belgeler.map((b) => {
                const etiket = b.tipi === 'diger' && b.ozelTip
                  ? b.ozelTip
                  : BELGE_TIPI_ETIKETLERI[b.tipi]
                const gun = b.gecerlilikTarihi ? gunSayisi(b.gecerlilikTarihi) : null
                const sureUyari = gun !== null && gun <= 30
                const sureDoldu = gun !== null && gun < 0
                const isResim = b.mimeType.startsWith('image/')
                const isPdf = b.mimeType === 'application/pdf'

                return (
                  <div key={b.id} className={`border rounded-xl p-3 ${
                    sureDoldu ? 'border-red-300 bg-red-50' :
                    sureUyari ? 'border-amber-300 bg-amber-50' :
                    'border-gray-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      {/* Önizleme: resim ise thumbnail, PDF ise icon */}
                      <a
                        href={b.dosyaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 group"
                        title="Büyük göster"
                      >
                        {isResim ? (
                          <img
                            src={b.dosyaUrl}
                            alt={b.dosyaAdi}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200 group-hover:border-brand-500 transition-colors"
                            loading="lazy"
                          />
                        ) : (
                          <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
                            isPdf ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                          } group-hover:opacity-80 transition-opacity`}>
                            <FileText className="w-7 h-7" />
                          </div>
                        )}
                      </a>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm text-gray-900">{etiket}</p>
                          {sureDoldu && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-red-200 text-red-900 rounded-full font-bold">
                              SÜRESİ DOLDU
                            </span>
                          )}
                          {sureUyari && !sureDoldu && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded-full font-bold">
                              {gun} gün kaldı
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 truncate mt-0.5">{b.dosyaAdi}</p>
                        <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500 flex-wrap">
                          <span>{(b.boyut / 1024).toFixed(0)} KB</span>
                          <span>•</span>
                          <span>
                            {b.yuklemeTarihi.toDate().toLocaleDateString('tr-TR', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })}
                          </span>
                          {b.gecerlilikTarihi && (
                            <>
                              <span>•</span>
                              <span className="inline-flex items-center gap-0.5">
                                <Calendar className="w-3 h-3" />
                                {new Date(b.gecerlilikTarihi).toLocaleDateString('tr-TR', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <a
                          href={b.dosyaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-blue-100 rounded-lg text-gray-600 hover:text-blue-600 transition-colors"
                          title="Görüntüle"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => indir(b)}
                          disabled={indiriliyor === b.id}
                          className="p-2 hover:bg-green-100 rounded-lg text-gray-600 hover:text-green-600 transition-colors disabled:opacity-50"
                          title="İndir"
                        >
                          {indiriliyor === b.id ? (
                            <div className="w-4 h-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent"></div>
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                        {silebilir && (
                          <button
                            onClick={() => setSilinecek(b)}
                            className="p-2 hover:bg-red-100 rounded-lg text-gray-600 hover:text-red-600 transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {!yukleyebilir && belgeler.length > 0 && (
            <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-600">
                Belge yükleme yetkin yok.
              </p>
            </div>
          )}
        </div>
      </Modal>

      <BelgeYukleModal
        acik={yukleModalAcik}
        personel={personel}
        onKapat={() => setYukleModalAcik(false)}
        onYuklendi={() => {
          setYukleModalAcik(false)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!silinecek}
        baslik="Belgeyi Sil"
        mesaj={`"${silinecek?.dosyaAdi}" silinecek. Geri alınamaz. Devam mı?`}
        onayMetni="Sil"
        tehlikeli
        onOnay={silOnay}
        onIptal={() => setSilinecek(null)}
      />
    </>
  )
}
